/**
 * The fixture lists on competition, league and schedule pages, kept current:
 * fixtures leave the list once they start, and in a list grouped by month
 * (a competition's) or by day (the schedule) each sits under its month or
 * day in the visitor's own time zone, which can differ from the UTC one the
 * server grouped by. On a competition page Next up counts down to the first
 * fixture left.
 *
 * The rows themselves are the data: each has its start in a <time>, its
 * title, and its label in data-label.
 */
import type { UpcomingEvent } from "../core/upcoming.ts";
import { everyMinute, renderNext, startTime } from "./countdown.ts";

const pad = (n: number): string => String(n).padStart(2, "0");
const monthFormat = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });
const dayFormat = new Intl.DateTimeFormat(undefined, { weekday: "long", day: "numeric", month: "long" });

/** "2026-10-25" for a local date. */
const dayKey = (at: Date): string =>
  `${String(at.getFullYear())}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`;

/** The local date a "2026-10" or "2026-10-25" key starts on. */
function keyDate(key: string): Date {
  const [year = 0, month = 1, day = 1] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** How a list is grouped: the key a fixture's start falls under, and that group's heading. */
interface Grouping {
  key: (start: Date) => string;
  title: (key: string, now: Date) => string;
}

const GROUPINGS: Record<string, Grouping> = {
  month: {
    key: (start) => dayKey(start).slice(0, 7),
    title: (key) => monthFormat.format(keyDate(key)),
  },
  day: {
    key: dayKey,
    // "Today · Sunday 25 October", "Tomorrow · Monday 26 October", then the date alone.
    title: (key, now) => {
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const relative = { [dayKey(now)]: "Today · ", [dayKey(tomorrow)]: "Tomorrow · " }[key] ?? "";
      return relative + dayFormat.format(keyDate(key)).replace(/,/g, "");
    },
  },
};

/** The fixture a row shows. */
function rowEvent(row: HTMLElement): UpcomingEvent | undefined {
  const time = row.querySelector("time");
  if (!time) return undefined;
  return {
    series: "",
    seriesName: "",
    headline: row.querySelector(".fixture-title")?.textContent ?? "",
    label: row.dataset["label"] ?? "",
    start: time.dateTime,
    allDay: !time.dateTime.includes("T"),
  };
}

/** A new, empty group like the list's others: its heading and its list. */
function newGroup(key: string, titleTag: string): HTMLElement {
  const group = document.createElement("div");
  group.className = "fixture-group";
  group.dataset["key"] = key;
  const title = document.createElement(titleTag);
  title.className = "group-title";
  const list = document.createElement("ol");
  list.className = "fixtures";
  list.setAttribute("role", "list");
  group.append(title, list);
  return group;
}

/** Whether the groups on the page already hold exactly these rows, in this order. */
function sameGrouping(list: HTMLElement, groups: Map<string, HTMLElement[]>): boolean {
  const shown = [...list.querySelectorAll<HTMLElement>(".fixture-group")];
  const wanted = [...groups];
  return (
    shown.length === wanted.length &&
    shown.every((group, i) => {
      const [key, rows] = wanted[i] ?? ["", []];
      const items = [...group.querySelectorAll(".fixture")];
      return (
        group.dataset["key"] === key &&
        items.length === rows.length &&
        items.every((row, j) => row === rows[j])
      );
    })
  );
}

/**
 * Drops started fixtures and, in a grouped list, regroups the rest by the
 * visitor's own months or days, soonest first; returns the soonest left.
 *
 * The server orders all-day fixtures from midnight UTC, but they start at
 * the visitor's own midnight, so west of UTC an all-day fixture can come
 * after an evening kick-off listed below it: rows are sorted afresh here.
 */
function arrange(list: HTMLElement, grouping: Grouping | undefined, now: Date): UpcomingEvent | undefined {
  const upcoming: { row: HTMLElement; event: UpcomingEvent; start: Date }[] = [];
  for (const row of list.querySelectorAll<HTMLElement>(".fixture")) {
    const event = rowEvent(row);
    if (!event) continue;
    const start = startTime(event);
    if (start < now) row.remove();
    else upcoming.push({ row, event, start });
  }
  // A stable sort: fixtures at the same moment keep the server's order.
  upcoming.sort((a, b) => a.start.getTime() - b.start.getTime());
  const next = upcoming[0]?.event;

  const groups = new Map<string, HTMLElement[]>();
  for (const { row, start } of grouping ? upcoming : []) {
    const key = grouping?.key(start) ?? "";
    const rows = groups.get(key);
    if (rows) rows.push(row);
    else groups.set(key, [row]);
  }
  if (!grouping) return next;

  if (!sameGrouping(list, groups)) {
    const existing = new Map(
      [...list.querySelectorAll<HTMLElement>(".fixture-group")].map((group) => [
        group.dataset["key"] ?? "",
        group,
      ]),
    );
    const titleTag = list.querySelector(".group-title")?.tagName.toLowerCase() ?? "h3";
    for (const [key, rows] of groups) {
      const group = existing.get(key) ?? newGroup(key, titleTag);
      existing.delete(key);
      group.querySelector("ol")?.append(...rows);
      list.append(group);
    }
    for (const group of existing.values()) group.remove();
  }

  for (const group of list.querySelectorAll<HTMLElement>(".fixture-group")) {
    const title = group.querySelector(".group-title");
    const text = grouping.title(group.dataset["key"] ?? "", now);
    if (title && title.textContent !== text) title.textContent = text;
  }
  return next;
}

/** Next up once every fixture on the page has started. */
function showNothingNext(card: HTMLElement): void {
  const label = document.createElement("p");
  label.className = "next-when";
  label.id = "next-up-label";
  label.textContent = "Next up";
  const title = document.createElement("h2");
  title.className = "next-title";
  title.textContent = "Nothing scheduled";
  const more = document.createElement("p");
  more.className = "next-label";
  more.textContent = "Check back soon";
  card.classList.add("next-up-empty");
  card.replaceChildren(label, title, more);
}

/** Keeps a page's fixture list, and a competition page's Next up, current. */
export function startFixtureList(): void {
  const list = document.querySelector<HTMLElement>(".fixture-list");
  if (!list) return;
  const grouping = GROUPINGS[list.dataset["group"] ?? ""];
  everyMinute(() => {
    const now = new Date();
    const next = arrange(list, grouping, now);
    const card = document.querySelector<HTMLElement>(".comp-body > .next-up");
    if (!card) return;
    if (next) {
      renderNext(next, now);
    } else if (!card.classList.contains("next-up-empty")) {
      showNothingNext(card);
      list.closest(".comp-body")?.classList.add("comp-body-empty");
      list.remove();
    }
  });
}
